/**
 * RegistrarDriftPanel
 *
 * Embedded admin panel that lists unresolved registrar drifts produced by
 * the P3 scheduled reconciliation job. Each row exposes a "Resolve" action
 * with an optional-notes dialog; submitting invalidates the drift queries
 * so the table refreshes.
 *
 * Renders as a standalone card so it can be dropped into the existing
 * `DomainOperationsDashboardPage` without restructuring it.
 */

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  useAdminRegistrarDrifts,
  useResolveRegistrarDrift,
} from "@/hooks/useAdminRegistrarDriftQueries";
import { DomainDriftResolveDialog } from "./DomainDriftResolveDialog";
import type { RegistrarDriftDto } from "@/types/domain-drift";
import { formatDateTime } from "@/lib/utils";

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return formatDateTime(value);
}

function formatValue(value: string | null): string {
  if (value === null || value === "") return "—";
  return value;
}

export function RegistrarDriftPanel() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [target, setTarget] = useState<RegistrarDriftDto | null>(null);

  const filters = { resolved: false, page, pageSize };
  const { data, isLoading, error, refetch } = useAdminRegistrarDrifts(filters);
  const resolveMutation = useResolveRegistrarDrift();

  const drifts = data?.data ?? [];
  const pagination = data?.pagination;
  const totalCount = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.currentPage ?? 1;

  const handleResolve = (notes: string | undefined) => {
    if (!target) return;
    resolveMutation.mutate(
      { id: target.id, data: { notes } },
      { onSuccess: () => setTarget(null) },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Registrar drift ({totalCount})
            </CardTitle>
            <CardDescription>
              Differences detected between WiCloud and the registrar by the
              scheduled reconciliation job. Resolve a row once the underlying
              cause has been reconciled.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="py-8 text-center">
            <p className="text-destructive-ink">Failed to load drift rows</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {(error as Error).message}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <p className="py-8 text-center text-muted-foreground">
            Loading drift rows...
          </p>
        ) : drifts.length === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">No unresolved drift</p>
            <p className="text-sm text-muted-foreground">
              The reconciliation job has not flagged any registrar drift.
            </p>
          </div>
        ) : (
          <TooltipProvider>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Domain</TableHead>
                  <TableHead scope="col">Drift type</TableHead>
                  <TableHead scope="col">Local value</TableHead>
                  <TableHead scope="col">Registrar value</TableHead>
                  <TableHead scope="col">Detected</TableHead>
                  <TableHead scope="col" className="text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drifts.map((drift) => {
                  const busy =
                    resolveMutation.isPending &&
                    resolveMutation.variables?.id === drift.id;
                  return (
                    <TableRow key={drift.id}>
                      <TableCell className="font-medium">
                        {drift.fqdn ?? drift.domainRegistrationId}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{drift.driftType}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <code className="block truncate text-xs">
                              {formatValue(drift.localValue)}
                            </code>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm break-all">
                            {formatValue(drift.localValue)}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <code className="block truncate text-xs">
                              {formatValue(drift.registrarValue)}
                            </code>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm break-all">
                            {formatValue(drift.registrarValue)}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatDate(drift.detectedAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => setTarget(drift)}
                        >
                          Resolve
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </TooltipProvider>
        )}
      </CardContent>

      <DomainDriftResolveDialog
        drift={target}
        isSubmitting={resolveMutation.isPending}
        onClose={() => setTarget(null)}
        onSubmit={handleResolve}
      />
    </Card>
  );
}
